import { useSelect } from '@refinedev/antd';
import { Select, type SelectProps } from 'antd';
import type { CrudFilter, BaseKey } from '@refinedev/core';
import type { IFacility } from '@admin-panel/types';

export const ALL_FACILITIES_VALUE = 'ALL_FACILITIES';

export type FacilitySelectValue = BaseKey | Array<BaseKey> | undefined;

export type FacilitySelectProps = Omit<SelectProps<FacilitySelectValue>, 'options'> & {
  filters?: Array<CrudFilter>;
  showAllFacilitiesOption?: boolean;
  onAllFacilitiesSelected?: (facilityIds: Array<number>) => void;
  setFacility?: (facility: IFacility | undefined) => void;
  selectedFacility?: IFacility;
};

export const FacilitySelect = ({
  filters: additionalFilters,
  showAllFacilitiesOption,
  onAllFacilitiesSelected,
  setFacility,
  onChange,
  onSelect,
  onDeselect,
  value,
  defaultValue,
  allowClear = true,
  showSearch = true,
  placeholder = 'Choose Facility',
  selectedFacility: selectedFacilityProp,
  ...restSelectProps
}: FacilitySelectProps) => {
  const filters: Array<CrudFilter> = [
    { field: 'enabled', value: 'true', operator: 'eq' },
    ...(additionalFilters ?? []),
  ];

  const normalizePrimitive = (raw: BaseKey) => {
    if (raw === ALL_FACILITIES_VALUE) {
      return raw;
    }

    const asNumber = Number(raw);
    return Number.isNaN(asNumber) ? raw : asNumber;
  };

  const normalizeValue = (rawValue: FacilitySelectValue | null): FacilitySelectValue => {
    if (rawValue === ALL_FACILITIES_VALUE) {
      return rawValue;
    }

    if (rawValue === undefined || rawValue === null || rawValue === '') {
      return undefined;
    }

    if (Array.isArray(rawValue)) {
      const normalized = rawValue
        .map(value => {
          if (value === ALL_FACILITIES_VALUE) {
            return value;
          }

          if (value === undefined || value === null || value === '') {
            return undefined;
          }

          return normalizePrimitive(value);
        })
        .filter((value): value is BaseKey => value !== undefined);

      return normalized.length ? normalized : undefined;
    }

    return normalizePrimitive(rawValue);
  };

  const normalizedValue = normalizeValue(value);
  const normalizedDefaultValue = normalizeValue(defaultValue ?? value);

  const { selectProps, query } = useSelect<IFacility>({
    resource: 'facilities',
    defaultValue:
      normalizedDefaultValue === ALL_FACILITIES_VALUE ? undefined : normalizedDefaultValue,
    pagination: {
      pageSize: 1000,
    },
    filters,
    optionLabel: 'name',
    optionValue: 'id',
    debounce: 500,
    onSearch: searchValue => [
      {
        field: 'name',
        operator: 'startswith',
        value: searchValue,
      },
    ],
  });

  const facilityOptions = query.data?.data as Array<IFacility> | undefined;

  const handleChange: SelectProps<FacilitySelectValue>['onChange'] = (nextValue, option) => {
    if (nextValue === undefined) {
      onChange?.(nextValue, option);
      return;
    }

    if (nextValue === ALL_FACILITIES_VALUE) {
      const allFacilityIds = facilityOptions?.map(facility => facility.id) ?? [];
      onChange?.(nextValue, option);
      onAllFacilitiesSelected?.(allFacilityIds);
      return;
    }

    if (Array.isArray(nextValue) && nextValue.length === 0) {
      onChange?.(nextValue, option);
      return;
    }

    const currentSelectedValue = Array.isArray(nextValue)
      ? nextValue[nextValue.length - 1]
      : nextValue;

    if (currentSelectedValue !== undefined) {
      const selectedFacility = facilityOptions?.find(
        facility => facility.id === Number(currentSelectedValue),
      );

      if (selectedFacility) {
        setFacility?.(selectedFacility);
      }
    }

    onChange?.(nextValue, option);
  };

  const enhancedSelectProps: SelectProps<FacilitySelectValue> = {
    ...(selectProps as SelectProps<FacilitySelectValue>),
    options: showAllFacilitiesOption
      ? [{ label: 'All Facilities', value: ALL_FACILITIES_VALUE }, ...(selectProps.options || [])]
      : selectProps.options,
  };

  // Keep options as-is so vendor changes don't retain stale facilities.

  return (
    <Select
      {...enhancedSelectProps}
      {...restSelectProps}
      onChange={handleChange}
      onSelect={onSelect}
      onDeselect={onDeselect ?? onSelect}
      placeholder={placeholder}
      showSearch={showSearch}
      allowClear={allowClear}
      value={normalizedValue}
      defaultValue={normalizedDefaultValue}
    />
  );
};
